import { FitnessCenterRounded, Redo } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  TextField,
  Button,
  IconButton,
  useMediaQuery,
  Card,
  InputAdornment,
  Alert,
  Autocomplete,
  Typography,
  DialogTitle,
  Dialog,
  Stack
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { mobileWidth, ProgressButton } from "@qubit/autoparts";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { warehouseService } from "~/api/warehouse";
import { useAppDispatch, useAppSelector } from "~/app/store";

import { useDebounceSearch } from "~/hooks/useDebounce";
import { useBarcodeScanner } from "~/lib/barCodeScan";
import { runAsyncSafely } from "~/lib/helpers";
import {
  mixpanelTrack,
  mixpanelTrackModalDismiss,
  useMixpanelModalName,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { QUANTITY_MAX_VALUE } from "~/lib/shared";
import { moveInventory } from "~/redux/actions/inventory";

import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { InventorySummaryDto, SearchBinRecord } from "~/types/api";

import { setIsMovePanelOpen } from "./inventory.slice";

const QtyButton = styled(Button)(({ theme }) => ({
  margin: "0 5px",
  fontSize: 18,
  [theme.breakpoints.up("md")]: {
    fontSize: 22
  }
}));

const IncrementButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  background: "white",
  border: `2px solid ${theme.palette.primary.main}`,
  marginRight: 10,
  "&:hover": {
    backgroundColor: theme.palette.secondary.main
  },
  fontSize: 20, // default
  [theme.breakpoints.up("sm")]: {
    fontSize: 32,
    marginRight: 14
  }
}));

const ButtonRow = styled(Box)(() => ({
  display: "flex",
  justifyContent: "flex-end"
}));

export type Props = {
  invToModify: InventorySummaryDto;
  refreshCb: ({
    inventoryWasEmptied
  }: {
    inventoryWasEmptied: boolean;
  }) => Promise<void>;
};

export function MoveInventoryButton(props: Props) {
  const { invToModify, refreshCb } = props;

  const trackedPageName = useMixpanelPageName();
  const trackedModalName = useMixpanelModalName("Move Inventory");
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isMobile = useMediaQuery(mobileWidth);

  const workstationId = useAppSelector(selectThisWorkstation)?.id;
  const isOpen = useAppSelector((state) => state.inventoryNew.isMovePanelOpen);

  const [quantity, updateQuantity] = useState<number | null>(null);
  const [weight, updateWeight] = useState<number | null>(null);
  const [newLocation, setNewLocation] = useState<SearchBinRecord | null>(null);
  const [options, updateOptions] = useState<SearchBinRecord[]>([]);
  const [binFieldValue, setBinFieldValue] = useState<string | null>(null);
  const debouncedInput = useDebounceSearch(binFieldValue);
  const maxQty =
    (invToModify?.count?.value || 0) -
    (invToModify?.committedCount?.value || 0);
  const overCommitted = quantity && quantity > maxQty;
  const disabled = overCommitted || !newLocation || !quantity;

  const handleQuantityChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newValue = event.target.value ? Number(event.target.value) : null;
    if (newValue !== null && newValue > QUANTITY_MAX_VALUE) {
      return;
    }
    updateQuantity(newValue);
  };

  const handleWeightChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    updateWeight(event.target.value ? Number(event.target.value) : null);
  };

  const handleMoveInventory = (): Promise<void> | null => {
    if (newLocation && quantity && invToModify) {
      return dispatch(
        moveInventory(
          {
            count: {
              units: invToModify.count?.units || "",
              value: quantity
            },
            targetBinId: newLocation.bin_id
          },
          invToModify.inventoryId,
          newLocation.bin_type,
          workstationId
        )
      );
    }
    return null;
  };

  const handleClose = useCallback(() => {
    setNewLocation(null);
    setBinFieldValue(null);
    dispatch(setIsMovePanelOpen(false));
  }, [dispatch]);

  const searchBins = async (input: string): Promise<void> => {
    let binRecords: SearchBinRecord[] = [];
    if (input.length) {
      const binSearch = await warehouseService.get<SearchBinRecord[]>(
        `/api/bins/search/${input}`
      );
      binRecords = binSearch.data;
    }
    if (binRecords.length === 1) {
      setNewLocation(binRecords[0]);
    }
    // don't give user the option to move from autostore bin to autostore bin
    const filteredBinRecords =
      invToModify?.binType === "autostore"
        ? binRecords.filter((bin) => bin.bin_type !== "autostore")
        : binRecords;
    updateOptions(filteredBinRecords);
  };

  useEffect(() => {
    if (isOpen && debouncedInput) {
      void runAsyncSafely(searchBins(debouncedInput));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInput, open]);
  useBarcodeScanner<boolean>({
    disabled: !isOpen,
    findScanMatch: (buffer: string) => {
      setBinFieldValue(buffer);
      return true;
    }
  });

  const quantityToDisplay = quantity || (invToModify?.count ? "" : t("n/a"));

  const quantityTextField = (
    <TextField
      label={t("quantity")}
      type="number"
      variant="outlined"
      value={quantityToDisplay}
      onChange={handleQuantityChange}
      sx={{ width: "100%" }}
      slotProps={{
        inputLabel: {
          shrink: true
        },
        input: {
          sx: {
            "& input": {
              fontSize: {
                xs: 30,
                sm: 50
              },
              padding: {
                xs: "15px 10px",
                sm: "25px 10px 32px 18px",
                md: "35px 10px 35px 18px"
              }
            }
          }
        }
      }}
      disabled={!invToModify || !invToModify.count}
      onBlur={() => {
        if (quantity) {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            label: "Manual Quantity Input",
            type: "Input Change"
          });
        }
      }}
    />
  );

  const quantityButtons = (
    <Stack
      direction="column"
      gap={1}
      sx={{
        position: "absolute",
        top: {
          xs: 0,
          sm: 18,
          md: 15
        },
        right: {
          xs: 0,
          sm: 8
        },
        flexWrap: "nowrap"
      }}
    >
      <ButtonRow>
        <IncrementButton
          onClick={(): void => {
            const newQty = quantity ? quantity + 1 : 1;
            if (newQty > QUANTITY_MAX_VALUE) return;
            mixpanelTrack({
              trackedPageName: trackedModalName,
              label: "Increase Quantity",
              type: "Button Click",
              eventProperties: {
                "Previous Quantity": quantity,
                "New Quantity": newQty
              }
            });
            updateQuantity(newQty);
          }}
          size="large"
          aria-label="increase quantity"
          disabled={quantity !== null && quantity >= QUANTITY_MAX_VALUE}
        >
          <ExpandLessIcon sx={{ fontSize: { xs: 20, md: 28 } }} />
        </IncrementButton>
        <QtyButton
          variant="contained"
          color="primary"
          onClick={(): void => {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              label: "Set Quantity to 6",
              type: "Button Click",
              eventProperties: {
                "Previous Quantity": quantity,
                "New Quantity": 6
              }
            });
            updateQuantity(6);
          }}
        >
          6
        </QtyButton>
        <QtyButton
          variant="contained"
          color="primary"
          onClick={(): void => {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              label: "Set Quantity to 8",
              type: "Button Click",
              eventProperties: {
                "Previous Quantity": quantity,
                "New Quantity": 8
              }
            });
            updateQuantity(8);
          }}
        >
          8
        </QtyButton>
      </ButtonRow>
      <ButtonRow>
        <IncrementButton
          onClick={(): void => {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              label: "Decrease Quantity",
              type: "Button Click",
              eventProperties: {
                "Previous Quantity": quantity,
                "New Quantity": quantity ? quantity - 1 : 0
              }
            });
            updateQuantity(quantity ? quantity - 1 : 0);
          }}
          size="large"
          aria-label="decrease quantity"
        >
          <ExpandMoreIcon sx={{ fontSize: { xs: 20, md: 28 } }} />
        </IncrementButton>
        <QtyButton
          variant="contained"
          color="primary"
          onClick={(): void => {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              label: "Set Quantity to 24",
              type: "Button Click",
              eventProperties: {
                "Previous Quantity": quantity,
                "New Quantity": 24
              }
            });
            updateQuantity(24);
          }}
        >
          24
        </QtyButton>
        <QtyButton
          variant="contained"
          color="primary"
          onClick={(): void => {
            mixpanelTrack({
              trackedPageName: trackedModalName,
              label: "Set Quantity to 36",
              type: "Button Click",
              eventProperties: {
                "Previous Quantity": quantity,
                "New Quantity": 36
              }
            });
            updateQuantity(36);
          }}
        >
          36
        </QtyButton>
      </ButtonRow>
    </Stack>
  );

  const weightTextField = (
    <TextField
      label={t("weight")}
      type="number"
      fullWidth
      variant="outlined"
      value={weight || ""}
      onChange={handleWeightChange}
      disabled={disabled}
      sx={{ width: "100%" }}
      slotProps={{
        inputLabel: {
          shrink: true
        },
        input: {
          sx: {
            "& input": {
              fontSize: {
                xs: 30,
                sm: 50
              },
              padding: {
                xs: "15px 10px",
                md: "35px 10px 35px 18px"
              }
            }
          },
          endAdornment: (
            <InputAdornment position="end">
              <FitnessCenterRounded
                color="disabled"
                style={{
                  fontSize: 25
                }}
              />
            </InputAdornment>
          )
        }
      }}
      onBlur={() => {
        if (weight) {
          mixpanelTrack({
            trackedPageName: trackedModalName,
            label: "Manual Weight Input",
            type: "Input Change"
          });
        }
      }}
    />
  );

  const MoveInventoryButton = (
    <ProgressButton
      buttonSize={isMobile ? "large" : "xLarge"}
      emphasis="high"
      responsive
      variant="contained"
      color="primary"
      fullWidth
      startIcon={<Redo style={{ fontSize: 26 }} />}
      disabled={disabled}
      onClick={async () => {
        mixpanelTrack({
          trackedPageName: trackedModalName,
          label: "Move",
          type: "Button Click"
        });
        await handleMoveInventory();
        await refreshCb({ inventoryWasEmptied: quantity === maxQty });
        handleClose();
      }}
    >
      {t("move")}
    </ProgressButton>
  );

  const warningText = `${maxQty} is the maximum number that can be moved.  ${
    invToModify?.committedCount?.value || 0
  } units are committed.`;

  return (
    <>
      <Button
        color="secondary"
        onClick={(): void => {
          mixpanelTrack({
            trackedPageName,
            label: "Move Inventory",
            type: "Button Click"
          });
          dispatch(setIsMovePanelOpen(true));
        }}
        style={{
          flexBasis: "auto",
          width: "175px"
        }}
      >
        {t("move")}
      </Button>
      <Dialog
        onClose={(_e, reason) => {
          mixpanelTrackModalDismiss(trackedModalName, reason);
          handleClose();
        }}
        open={isOpen}
        slotProps={{
          paper: {
            sx: {
              margin: {
                xs: 2,
                md: 4
              }
            }
          }
        }}
      >
        <Card
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <DialogTitle
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              textAlign: "center"
            }}
          >
            <Typography variant="h6">{t("move inventory")}</Typography>
            <IconButton
              onClick={() => {
                mixpanelTrack({
                  trackedPageName: trackedModalName,
                  label: "Close",
                  type: "Button Click"
                });
                handleClose();
              }}
              sx={{
                position: "absolute",
                right: 5
              }}
            >
              <CloseIcon sx={{ color: "primary.main" }} />
            </IconButton>
          </DialogTitle>
          <Stack
            padding={{
              xs: 3,
              md: 4
            }}
            flexDirection="column"
            gap={2}
          >
            <Autocomplete<SearchBinRecord>
              options={options}
              filterOptions={(ops) => ops}
              getOptionLabel={(option: SearchBinRecord): string =>
                option.location
              }
              style={{
                background: "white"
              }}
              onChange={(
                e: ChangeEvent<unknown>,
                option: SearchBinRecord | null
              ): void => {
                if (e) {
                  if (option) {
                    mixpanelTrack({
                      trackedPageName: trackedModalName,
                      label: "Select Bin Location",
                      type: "Select Search Result",
                      eventProperties: {
                        "Searched Text": binFieldValue,
                        "Selected Location": option?.location
                      }
                    });
                  }
                  setNewLocation(option || null);
                  setBinFieldValue(null);
                }
              }}
              popupIcon={null}
              inputValue={binFieldValue || newLocation?.location || ""}
              blurOnSelect
              isOptionEqualToValue={(option, value): boolean =>
                [option.location.replace(" ", ""), option.location].includes(
                  value.location
                )
              }
              onBlur={() => {
                if (!!binFieldValue?.length && !newLocation) {
                  mixpanelTrack({
                    trackedPageName: trackedModalName,
                    label: "Search Bin Location",
                    type: "Search",
                    eventProperties: {
                      "Searched Text": binFieldValue,
                      "Selected Location": null
                    }
                  });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="subInput"
                  onChange={(e): void => {
                    setBinFieldValue(e.target.value);
                    setNewLocation(null);
                  }}
                  label={` ${t("search")} ${t("locations")}`}
                  variant="outlined"
                  fullWidth
                />
              )}
            />
            <Box
              style={{
                position: "relative"
              }}
            >
              {quantityTextField}
              {!isMobile && invToModify && invToModify.count && quantityButtons}
            </Box>
            {weightTextField}
            <Stack
              direction="column"
              gap={2}
              sx={{
                mt: {
                  xs: 1,
                  md: 0
                }
              }}
            >
              {!!overCommitted && (
                <Alert
                  severity="warning"
                  sx={{
                    fontSize: { xs: 15, sm: 18, md: 22 },
                    padding: { xs: "3px 8px", sm: "6px 16px" }
                  }}
                >
                  {warningText}
                </Alert>
              )}
              {MoveInventoryButton}
            </Stack>
          </Stack>
        </Card>
      </Dialog>
    </>
  );
}
